import React, { useState, useEffect } from 'react';
import {
  Shield,
  Smartphone,
  QrCode,
  Settings,
  Scan,
  Edit3,
  CheckCircle,
  AlertCircle,
  XCircle,
  Users,
  Key,
  Trash2,
  RefreshCw
} from 'lucide-react';

const NFCApp = () => {
  const [mode, setMode] = useState('idle'); // idle, reading, writing
  const [selectedRole, setSelectedRole] = useState('employee');
  const [nfcData, setNfcData] = useState(null);
  const [qrInput, setQrInput] = useState('');
  const [manualInput, setManualInput] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [status, setStatus] = useState('Ready');
  const [lastOperation, setLastOperation] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [adminMode, setAdminMode] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);

  const roles = [
    { id: 'employee', name: 'Employee', color: 'bg-blue-500', access: 'basic' },
    { id: 'manager', name: 'Manager', color: 'bg-teal-500', access: 'elevated' },
    { id: 'admin', name: 'Administrator', color: 'bg-purple-500', access: 'full' },
    { id: 'security', name: 'Security', color: 'bg-red-500', access: 'security' },
    { id: 'visitor', name: 'Visitor', color: 'bg-gray-600', access: 'limited' }
  ];

  // Simulate NFC availability check
  useEffect(() => {
    const checkNFC = async () => {
      // Simulate NFC check
      setTimeout(() => {
        setIsConnected(true);
        setStatus('NFC Ready');
      }, 1000);
    };
    checkNFC();
  }, []);

  const simulateNFCRead = async () => {
    setMode('reading');
    setStatus('Hold your card near the device...');

    if ('NDEFReader' in window) {
      try {
        const ndef = new NDEFReader();

        // Optional: wrap onreading in try/catch to catch runtime errors
        ndef.onreading = (event) => {
          try {
            const decoder = new TextDecoder();
            const records = event.message.records;

            if (!records || records.length === 0) {
              throw new Error('No NDEF records found on this tag');
            }

            const record = records[1];
            const payload = record.data ? decoder.decode(record.data) : 'unknown';

            const realData = {
              id: `NFC_${Math.random().toString(36).substr(2, 8).toUpperCase()}`,
              payload: JSON.parse(payload),
              timestamp: new Date().toISOString(),
              type: record.recordType || 'unknown'
            };

            setNfcData(realData);
            setLastOperation({ type: 'read', data: realData, time: new Date() });
            verifyCard(realData);
            setMode('idle');
            setStatus('✅ NFC Tag read successfully!');
          } catch (innerErr) {
            console.error(innerErr);
            setMode('idle');
            setStatus('❌ Error processing NFC tag: ' + innerErr.message);
          }
        };

        ndef.onerror = (event) => {
          setMode('idle');
          setStatus('❌ NFC scan error: ' + (event.message || 'Unknown error'));
        };

        await ndef.scan(); // Start scanning
      } catch (err) {
        setMode('idle');
        setStatus('❌ NFC not available or scan failed: ' + err.message);
      }
    } else {
      setMode('idle');
      setStatus('❌ Web NFC is not supported on this device/browser.');
    }
  };

  //   const simulateNFCRead = async () => {
  //     setMode('reading');
  //     setStatus('Hold your card near the device...');

  //     // Simulate card detection delay
  //     setTimeout(() => {
  //       const mockData = {
  //         id: `NTAG215_${Math.random().toString(36).substr(2, 8).toUpperCase()}`,
  //         role: roles[Math.floor(Math.random() * roles.length)].id,
  //         timestamp: new Date().toISOString(),
  //         type: 'NTAG215'
  //       };

  //       setNfcData(mockData);
  //       setLastOperation({ type: 'read', data: mockData, time: new Date() });
  //       verifyCard(mockData);
  //       setMode('idle');
  //     }, 2000);
  //   };

  const verifyCard = async (cardData) => {
    setStatus('Verifying card...');
    setVerificationStatus('verifying');

    // Simulate server verification
    setTimeout(() => {
      const isValid = Math.random() > 0.3; // 70% success rate for demo

      if (isValid) {
        setVerificationStatus('success');
        setStatus('Card verified successfully');
        // Simulate main function call
        performMainFunction(cardData);
      } else {
        setVerificationStatus('failed');
        setStatus('Card verification failed');
      }
    }, 1500);
  };

  const performMainFunction = async (cardData) => {
    setTimeout(() => {
      setStatus(`Access granted for ${roles.find((r) => r.id === cardData.role)?.name || 'Unknown'}`);
      // Reset after showing result
      setTimeout(() => {
        setVerificationStatus(null);
        setStatus('Ready');
      }, 3000);
    }, 1000);
  };

  const simulateNFCWrite = async () => {
    const dataToWrite = qrInput || manualInput;
    if (!dataToWrite) {
      setStatus('No data to write');
      return;
    }

    setMode('writing');
    setStatus('Hold your card near the device to write...');

    setTimeout(() => {
      const writeData = {
        id: dataToWrite,
        role: selectedRole,
        timestamp: new Date().toISOString(),
        type: 'NTAG215',
        written: true
      };

      setNfcData(writeData);
      setLastOperation({ type: 'write', data: writeData, time: new Date() });
      setStatus('Data written successfully');
      setQrInput('');
      setManualInput('');
      setMode('idle');

      setTimeout(() => setStatus('Ready'), 2000);
    }, 2000);
  };

  const simulateQRScan = () => {
    // Simulate QR code scanning
    const mockQRData = `QR_${Math.random().toString(36).substr(2, 12).toUpperCase()}`;
    setQrInput(mockQRData);
    setStatus('QR Code scanned successfully');
    setTimeout(() => setStatus('Ready'), 2000);
  };

  const adminOverride = () => {
    setNfcData(null);
    setVerificationStatus(null);
    setLastOperation(null);
    setStatus('Card data cleared by admin');
    setTimeout(() => setStatus('Ready'), 2000);
  };

  const adminReset = () => {
    setMode('idle');
    setNfcData(null);
    setQrInput('');
    setManualInput('');
    setVerificationStatus(null);
    setLastOperation(null);
    setStatus('System reset by admin');
    setTimeout(() => setStatus('Ready'), 2000);
  };

  const getStatusColor = () => {
    if (verificationStatus === 'success') return 'text-green-400';
    if (verificationStatus === 'failed') return 'text-red-400';
    if (verificationStatus === 'verifying') return 'text-yellow-400';
    return 'text-blue-400';
  };

  const getStatusIcon = () => {
    if (verificationStatus === 'success') return <CheckCircle className="w-12 h-12 text-green-400" />;
    if (verificationStatus === 'failed') return <XCircle className="w-12 h-12 text-red-400" />;
    if (verificationStatus === 'verifying') return <RefreshCw className="w-12 h-12 text-yellow-400 animate-spin" />;
    return <Smartphone className="w-12 h-12 text-blue-400" />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-grey-900 via-blue-900 to-teal-900 p-8">
      <div className="max-w-sm mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-lg rounded-lg p-12 mb-10 border border-white/20 shadow-2xl">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-6">
              <div className="w-24 h-24 bg-gradient-to-r from-blue-500 to-teal-500 rounded-md flex items-center justify-center">
                <Smartphone className="w-12 h-12 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">NFCC Manager</h1>
                <p className="text-blue-200 text-sm">Corporate Access Control</p>
              </div>
            </div>
            <button
              onClick={() => setShowDrawer(true)}
              className="w-20 h-20 bg-white/10 rounded-md flex items-center justify-center border border-white/20 hover:bg-white/20 transition-all"
            >
              <Settings className="w-10 h-10 text-white" />
            </button>
          </div>

          {/* Status */}
          <div className="flex items-center space-x-6 bg-black/20 rounded-md p-12">
            {getStatusIcon()}
            <div>
              <p className={`font-medium ${getStatusColor()}`}>{status}</p>
              <p className="text-xs text-gray-300">{isConnected ? 'NFC Ready' : 'NFC Unavailable'}</p>
            </div>
          </div>
        </div>

        {/* Main Controls */}
        <div className="space-y-8 mb-10">
          {/* Read Button */}
          <button
            onClick={simulateNFCRead}
            disabled={mode !== 'idle'}
            className="w-full bg-gradient-to-r from-blue-500/20 to-teal-500/20 backdrop-blur-lg border border-blue-500/30 rounded-lg p-20 hover:from-blue-500/30 hover:to-teal-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed group"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-8">
                <div className="w-24 h-24 bg-blue-500/30 rounded-md flex items-center justify-center group-hover:bg-blue-500/50 transition-all">
                  <Scan className="w-12 h-12 text-blue-400" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold text-white">Read NFC Card</h3>
                  <p className="text-blue-200 text-sm">Scan and verify access</p>
                </div>
              </div>
              {mode === 'reading' && (
                <div className="w-12 h-12 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
              )}
            </div>
          </button>

          {/* Write Section */}
          <div className="bg-white/10 backdrop-blur-lg rounded-lg p-10 border border-white/20">
            <h3 className="text-lg font-semibold text-white mb-8 flex items-center">
              <Edit3 className="w-12 h-12 mr-4 text-teal-400" />
              Write to NFC Card
            </h3>

            {/* Role Selection */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-300 mb-4">Select Role</label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full bg-black/30 border border-gray-600 rounded px-6 py-4 text-white focus:border-teal-500 focus:outline-none"
              >
                {roles.map((role) => (
                  <option key={role.id} value={role.id} className="bg-gray-800">
                    {role.name} ({role.access})
                  </option>
                ))}
              </select>
            </div>

            {/* QR Code Input */}
            <div className="space-y-6 mb-8">
              <div className="flex space-x-4">
                <input
                  type="text"
                  placeholder="QR Code data will appear here..."
                  value={qrInput}
                  onChange={(e) => setQrInput(e.target.value)}
                  className="flex-1 bg-black/30 border border-gray-600 rounded px-6 py-4 text-white placeholder-gray-400 focus:border-teal-500 focus:outline-none"
                />
                <button
                  onClick={simulateQRScan}
                  className="w-20 h-20 p-2 bg-gradient-to-r from-teal-500 to-blue-500 rounded flex items-center justify-center hover:from-teal-600 hover:to-blue-600 transition-all"
                >
                  <QrCode className="w-12 h-12 text-white" />
                </button>
              </div>

              <div className="text-center text-gray-400 text-sm">OR</div>

              <input
                type="text"
                placeholder="Manual input for testing..."
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value)}
                className="w-full bg-black/30 border border-gray-600 rounded px-6 py-4 text-white placeholder-gray-400 focus:border-teal-500 focus:outline-none"
              />
            </div>

            <button
              onClick={simulateNFCWrite}
              disabled={mode !== 'idle' || (!qrInput && !manualInput)}
              className="w-full bg-gradient-to-r from-teal-500/20 to-blue-500/20 backdrop-blur-lg border border-teal-500/30 rounded-md p-4 hover:from-teal-500/30 hover:to-blue-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              <Edit3 className="w-10 h-10 text-teal-400" />
              <span className="text-white font-medium">{mode === 'writing' ? 'Writing...' : 'Write to Card'}</span>
              {mode === 'writing' && (
                <div className="w-8 h-8 border-2 border-teal-400 border-t-transparent rounded-full animate-spin"></div>
              )}
            </button>
          </div>
        </div>

        {/* Card Data Display */}
        {nfcData ? (
          <>
            <div className="bg-white/10 backdrop-blur-lg rounded-lg p-12 border border-white/20 mb-10">
              <h3 className="text-lg font-semibold text-white mb-8">Card Information</h3>
              <div className="space-y-6 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-300">Card ID:</span>
                  <span className="text-white font-mono">{nfcData.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Name:</span>
                  <span className="text-white font-mono">{nfcData.payload.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Role:</span>
                  <span
                    className={`px-4 py-2 rounded text-xs font-medium ${
                      roles.find((r) => r.name === nfcData.payload.role)?.color || 'bg-gray-600'
                    } text-white`}
                  >
                    {roles.find((r) => r.name === nfcData.payload.role)?.name || 'Unknown'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Type:</span>
                  <span className="text-white">{nfcData.type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Timestamp:</span>
                  <span className="text-white">{new Date(nfcData.timestamp).toLocaleString()}</span>
                </div>
              </div>
            </div>
            <div>
              <h3>NFC Data:</h3>
              <pre>{JSON.stringify(nfcData, null, 2)}</pre>
            </div>
          </>
        ) : null}

        {/* Admin Panel Toggle */}
        <button
          onClick={() => setAdminMode(!adminMode)}
          className="w-full mb-8 bg-red-500/20 backdrop-blur-lg border border-red-500/30 rounded-md p-6 hover:bg-red-500/30 transition-all flex items-center justify-center space-x-4"
        >
          <Shield className="w-10 h-10 text-red-400" />
          <span className="text-red-400 font-medium">{adminMode ? 'Hide Admin Panel' : 'Show Admin Panel'}</span>
        </button>

        {/* Admin Controls */}
        {adminMode && (
          <div className="bg-red-500/10 backdrop-blur-lg rounded-lg p-10 border border-red-500/30 mb-6">
            <h3 className="text-lg font-semibold text-red-400 mb-4 flex items-center">
              <Shield className="w-10 h-10 mr-2" />
              Admin Controls
            </h3>
            <div className="grid grid-cols-2 gap-6">
              <button
                onClick={adminOverride}
                className="bg-red-500/30 hover:bg-red-500/50 border border-red-500/50 rounded-md p-6 text-red-300 text-sm font-medium transition-all flex items-center justify-center space-x-2"
              >
                <Trash2 className="w-10 h-10" />
                <span>Override</span>
              </button>
              <button
                onClick={adminReset}
                className="bg-red-500/30 hover:bg-red-500/50 border border-red-500/50 rounded-md p-6 text-red-300 text-sm font-medium transition-all flex items-center justify-center space-x-2"
              >
                <RefreshCw className="w-10 h-10" />
                <span>Reset</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Drawer */}
      {showDrawer && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDrawer(false)}></div>
          <div className="absolute bottom-0 left-0 right-0 bg-white/10 backdrop-blur-xl border-t border-white/20 rounded-t-lg p-10 max-w-md mx-auto">
            <div className="w-120 h-4 bg-gray-400 rounded-full mx-auto mb-12"></div>

            <h2 className="text-lg font-bold text-white mb-10">System Information</h2>

            <div className="space-y-8">
              <div className="bg-white/10 rounded-md p-8">
                <h3 className="text-white font-semibold mb-4 flex items-center">
                  <Users className="w-8 h-8 mr-4 text-blue-300" />
                  Active Roles
                </h3>
                <div className="space-y-4">
                  {roles.map((role) => (
                    <div key={role.id} className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`w-8 h-8 rounded-full ${role.color}`}></div>
                        <span className="text-gray-300 text-sm">{role.name}</span>
                      </div>
                      <span className="text-xs text-gray-400 bg-gray-900/30 px-4 py-2 rounded-sm">{role.access}</span>
                    </div>
                  ))}
                </div>
              </div>

              {lastOperation && (
                <div className="bg-white/10 rounded-md p-8">
                  <h3 className="text-white font-semibold mb-4 flex items-center">
                    <Key className="w-8 h-8 mr-2 text-teal-400" />
                    Last Operation
                  </h3>
                  <div className="text-sm text-gray-300">
                    <p>
                      Type: <span className="text-white capitalize">{lastOperation.type}</span>
                    </p>
                    <p>
                      Card ID: <span className="text-white font-mono">{lastOperation.data.id}</span>
                    </p>
                    <p>
                      Time: <span className="text-white">{lastOperation.time.toLocaleTimeString()}</span>
                    </p>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => setShowDrawer(false)}
              className="w-full mt-10 bg-gradient-to-r from-blue-500 to-teal-500 rounded-md py-6 text-white font-medium hover:from-blue-600 hover:to-teal-600 transition-all"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NFCApp;
